import { IAppContext } from 'App/AppContext';
import {
  mapFromRisk,
  mapFromRiskContext,
  mapFromRiskDashboard,
  mapFromRisks,
  mapFromRisksArray,
  mapToRisk,
} from 'mappings/riskMapping';
import { AssetRiskDTO } from 'models/dto/asset/assetDTO';
import { RiskObjectiveDTO } from 'models/dto/Objective/objectiveRiskDTO';
import { RiskContextDTO } from 'models/dto/riskContextDTO';
import RiskDTO, { RiskControlDTO, RiskProcessDTO, RiskThemeDTO } from 'models/dto/riskDTO';
import RisksDTO, { RiskDashboardDTO } from 'models/dto/risksDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Risk from 'models/risk';
import { RiskContext } from 'models/riskContext';
import Risks, { RiskDashboard } from 'models/risks';
import AppError from 'utils/appError';
import { toApiDateTimeOptional } from 'utils/datetime';
import { sortOnCode } from 'utils/sorting';
import http from './httpService';

export async function apiGetRisks(accessToken: string, appContext: IAppContext): Promise<Risks> {
  try {
    const ar = await http.get<RisksDTO>(`/risks`, http.getRequestConfig(accessToken));

    return mapFromRisks(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRisksForIdList(accessToken: string): Promise<number[]> {
  try {
    const ar = await http.get<Record<number, string>>(`/risks/idlist`, http.getRequestConfig(accessToken));

    if (!ar.data) return [];

    return Object.entries(ar.data)
      .sort(([, a], [, b]) => sortOnCode(a, b))
      .map(([id]) => Number(id));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetNewRiskCode(accessToken: string, code: string): Promise<string> {
  try {
    const ar = await http.post<string>(`/risks/code`, { valueString: code }, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateRisk(editRisk: Risk, accessToken: string, appContext: IAppContext): Promise<Risk> {
  try {
    const riskDTO = mapToRisk(editRisk);
    const ar = await http.put<RiskDTO>(`/risks`, riskDTO, http.getRequestConfig(accessToken));

    return mapFromRisk(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddRisk(
  newRisk: Risk,
  sourceRiskId: number | undefined,
  accessToken: string,
  appContext: IAppContext,
): Promise<Risk> {
  try {
    const riskDTO = mapToRisk(newRisk);
    const ar = await http.post<RiskDTO>(
      `/risks?sourceRiskId=${sourceRiskId ?? 0}`,
      riskDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromRisk(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveRisk(risk: Risk, accessToken: string): Promise<boolean> {
  try {
    await http.post(`/risks/delete/${risk.riskId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRisk(riskId: number, accessToken: string, appContext: IAppContext): Promise<Risks> {
  try {
    if (!riskId || riskId <= 0) return new Risks();
    const ar = await http.get<RisksDTO>(`/risks/${riskId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return new Risks();

    return mapFromRisks(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRisksSimilarToScore(
  score: number,
  editMode: 'initial' | 'current' | 'goal' | undefined,
  excludeRiskId: number | undefined,
  accessToken: string,
  appContext: IAppContext,
): Promise<Risks> {
  try {
    let phase: number = 0;
    switch (editMode) {
      case 'initial':
        phase = 1;
        break;
      case 'current':
        phase = 2;
        break;
      case 'goal':
        phase = 3;
        break;
    }

    const ar = await http.get<RisksDTO>(
      `/risks/score/around/${score}/${phase}?excludeRiskId=${excludeRiskId}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar.data) return new Risks();
    ar.data.risks = ar.data.risks.map((_risk) => _risk ?? new RiskDTO());

    return mapFromRisks(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//method of fetching risks for Board
export async function apiGetRisksBoard(accessToken: string, appContext: IAppContext): Promise<Risks> {
  try {
    const ar = await http.get<RisksDTO>(`/risks/board`, http.getRequestConfig(accessToken));
    if (!ar.data) return new Risks();

    return mapFromRisks(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateRiskKanbanState(risk: Risk, accessToken: string): Promise<Risk> {
  try {
    await http.put<RiskDTO>(
      `/risks/${risk.riskId}/?riskState=${risk.riskStateId}&sortOrder=${risk.sortOrder}`,
      {},
      http.getRequestConfig(accessToken),
    );

    return risk;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveControlFromRisk(riskId: number, controlId: number, accessToken: string): Promise<void> {
  try {
    const body = new RiskControlDTO();
    body.riskId = riskId;
    body.controlId = controlId;
    await http.post<RiskControlDTO>(`/risks/control?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddControlToRisk(riskId: number, controlId: number, accessToken: string): Promise<void> {
  try {
    const body = new RiskControlDTO();
    body.riskId = riskId;
    body.controlId = controlId;
    await http.post<RiskControlDTO>(`/risks/control?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRisksForControl(
  controlId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<Risks> {
  try {
    const ar = await http.get<RisksDTO>(`/risks/control/${controlId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return new Risks();

    return mapFromRisks(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRiskDashboard(
  riskId: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<RiskDashboard> {
  try {
    const ar = await http.get<RiskDashboardDTO>(`/risks/${riskId}/dashboard`, {
      ...http.getRequestConfig(accessToken),
      params: {
        startDate: toApiDateTimeOptional(startDate),
        endDate: toApiDateTimeOptional(endDate),
      },
    });

    return mapFromRiskDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveThemeFromRisk(riskId: number, themeId: number, accessToken: string): Promise<boolean> {
  try {
    const body = new RiskThemeDTO();
    body.riskId = riskId;
    body.themeId = themeId;
    await http.post<RiskThemeDTO>(`/risks/theme?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddThemeToRisk(riskId: number, themeId: number, accessToken: string): Promise<boolean> {
  try {
    const body = new RiskThemeDTO();
    body.riskId = riskId;
    body.themeId = themeId;
    await http.post<RiskThemeDTO>(`/risks/theme?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Objective
export async function apiAddObjectiveToRisk(
  riskId: number,
  objectiveIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new RiskObjectiveDTO(riskId, objectiveIdTo);
    await http.post<RiskObjectiveDTO>(`/risks/objective?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveObjectiveFromRisk(
  riskId: number,
  objectiveIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new RiskObjectiveDTO(riskId, objectiveIdTo);
    await http.post<RiskObjectiveDTO>(`/risks/objective?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Process
export async function apiAddProcessToRisk(riskId: number, processIdTo: number, accessToken: string): Promise<boolean> {
  try {
    const body = new RiskProcessDTO(riskId, processIdTo);
    await http.post<RiskProcessDTO>(`/risks/process?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessFromRisk(
  riskId: number,
  processIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new RiskProcessDTO(riskId, processIdTo);
    await http.post<RiskProcessDTO>(`/risks/process?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Asset
export async function apiAddRiskToAsset(assetId: number, riskId: number, accessToken: string): Promise<boolean> {
  try {
    const body = new AssetRiskDTO(assetId, riskId);
    await http.post<AssetRiskDTO>(`/assets/risk?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveRiskFromAsset(assetId: number, riskId: number, accessToken: string): Promise<boolean> {
  try {
    const body = new AssetRiskDTO(assetId, riskId);
    await http.post<AssetRiskDTO>(`/assets/risk?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Context
export const apiGetRiskContext = async (
  riskId: number,
  accessToken: string,
  globalCache: GlobalDataCache,
): Promise<RiskContext> => {
  try {
    const ar = await http.get<RiskContextDTO>(`/risks/context/${riskId}`, http.getRequestConfig(accessToken));

    return mapFromRiskContext(ar.data, globalCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export async function apiGetRisksForAsset(
  assetId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<Risks> {
  try {
    if (!assetId || assetId <= 0) return new Risks();
    //this endpoint returns a plain array of risks instead of the RisksDTO wrapper
    const ar = await http.get<RiskDTO[]>(`/risks/asset/${assetId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return new Risks();

    const output = new Risks();
    output.risks = mapFromRisksArray(ar.data, appContext);

    return output;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetRiskExternalId(accessToken: string, externalId: string): Promise<number> {
  try {
    const ar = await http.post<number>(
      `/risks/externalid`,
      { valueString: externalId },
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
